/* ============================================================================
   VALIDADOR DETERMINISTA DE REGLAS SST  (sstDiagnostics.js)
   ---------------------------------------------------------------------------
   Implementa las reglas de validación que PUEDEN PASAR EN CUALQUIER SCRIPT
   (se observaron en HOTELES y VT-TAXI, pero aplican a todos). Corre 100% local,
   sin IA, sobre el fuente .sst. Devuelve un array de "diagnostics" con el mismo
   contrato que consume el prompt de los agentes y el modal del runtime/admin:

     { code, severity, message, sourceLine, raw, ...campos propios de la regla }

   severity ∈ 'error' | 'warning' | 'info'

   Es autocontenido a propósito (sin imports) para poder testearse y reusarse
   tanto en el admin (Revisión Dev / generador) como en el runtime del script.
   ============================================================================ */

/* Variables internas del sistema/control: NO son variables de usuario y no
   requieren DEFINE. Todo lo que empiece con @ se trata como control. */
const SYSTEM_VARS = new Set([
  '@DATE', '@TIME', '@DATA_ROW', '@OK', '@BTN_PRSD', '@SYSTEM_ERROR', '@ERROR',
  '@YEAR', '@MONTH', '@DAY', '@HOUR', '@MIN', '@SEC', '@ROW', '@COL', '@PCC',
  '@AGENT', '@SINE', '@LNIATA', '@CRLF', '@NULL', '@TAB', '@ESC', '@ENTER',
  '@CURSOR', '@CLIPBOARD', '@LINE', '@COUNT', '@INDEX', '@RESULT',
])

/* Tipos de dato válidos para la firma de un DEFINE (estándar Scribe + legacy). */
const DEFINE_TYPES = new Set([
  'ANY', 'ALPHA', 'ALPHANUM', 'ALPHANUMERIC', 'NUM', 'NUMERIC', 'NUMBER',
  'DECIMAL', 'DATE', 'TIME', 'TEXT', 'MASK', 'EMAIL', 'BOOL', 'LIST', 'CURRENCY',
  '*',
  'A', 'N', 'D', 'T', 'M',
  // tipos legacy combinados reales de los .sst
  'A/N', 'N/A', 'AN', 'A/N/S', 'ALPHANUM/S',
])

/* Obligatoriedad válida. */
const DEFINE_MAND = new Set(['MAND', 'OPT', 'REQ', 'REQUIRED', 'OPTIONAL', 'M', 'O'])

/* ---- helpers ---- */
function isComment(line) {
  return /^\s*;/.test(line)
}

/* Reemplaza el contenido de las comillas por espacios (preserva longitud/columna)
   para poder detectar variables [VAR] sin confundir regex como "[A-Z]{4}". */
function stripQuoted(line) {
  return String(line).replace(/"[^"]*"/g, (m) => ' '.repeat(m.length))
}

/* Extrae nombres de variable [VAR] de una línea (ya sin comillas). Excluye
   declaraciones tipo [X=...] (llevan '=' dentro del corchete). */
function extractVarRefs(lineNoQuotes) {
  const out = []
  const rx = /\[([#@]?[A-Za-z_][A-Za-z0-9_#]*)\]/g
  let m
  while ((m = rx.exec(lineNoQuotes)) !== null) out.push(m[1])
  return out
}

function baseName(name) {
  return String(name).replace(/#[DC]$/i, '')
}

function extractDefineBlocks(line) {
  const out = []
  const rx = /DEFINE\s*\[([^\]]*)\]/gi
  let match
  while ((match = rx.exec(line)) !== null) {
    out.push({
      content: String(match[1] || '').trim(),
      raw: match[0],
    })
  }
  return out
}

function parseDefineBlock(block) {
  const content = String(block?.content || '').trim()
  const eq = content.indexOf('=')
  if (eq < 0) return { ok: false, reason: 'missing_equals' }
  const name = content.slice(0, eq).trim()
  const signature = content.slice(eq + 1)
  const segs = signature.split(':')
  const type = (segs[0] || '').trim().toUpperCase()
  if (!name) return { ok: false, reason: 'missing_name' }
  if (!type) return { ok: false, reason: 'missing_type', name, signature }
  return {
    ok: true,
    name,
    signature,
    type,
    length: (segs[1] || '').trim(),
    mand: (segs[2] || '').trim().toUpperCase(),
    controlSuffix: (name.match(/#[DC]$/i) || [''])[0].toUpperCase(),
  }
}

function pathBaseName(name) {
  return String(name || '').split(/[\\/]/).pop()
}

function comparableDependencyName(name) {
  return pathBaseName(name)
    .replace(/\.(sst|ssc|sbr|csv|txt|json)$/i, '')
    .trim()
    .toLowerCase()
}

function hasProvidedFile(providedFiles, expected) {
  const expectedFull = String(expected || '').trim().toLowerCase()
  const expectedBase = pathBaseName(expectedFull)
  const expectedComparable = comparableDependencyName(expected)
  return (providedFiles || []).some((file) => {
    const current = String(file || '').trim().toLowerCase()
    return (
      current === expectedFull ||
      pathBaseName(current) === expectedBase ||
      comparableDependencyName(current) === expectedComparable
    )
  })
}

/* Normaliza chevrones legacy preservando la cantidad de líneas (para no romper
   los sourceLine). ¯...® → »...« ; deja el resto igual. */
function normalizeLines(source) {
  let s = String(source || '')
  if (/¯.+®/.test(s)) s = s.replace(/¯/g, '»').replace(/®/g, '«')
  return s.split(/\r?\n/)
}

/* Une DEFAULT partidos por continuación "+"  (…="A|"+ \n "B|"+ …). */
function collectDefaults(lines) {
  const out = []
  for (let i = 0; i < lines.length; i++) {
    if (isComment(lines[i])) continue
    const m = lines[i].match(/^\s*DEFAULT\s*\[([^\]]+)\]\s*=\s*(.*)$/i)
    if (!m) continue
    const start = i
    let value = m[2]
    while (/\+\s*$/.test(value) && i + 1 < lines.length) {
      i++
      value = value.replace(/\+\s*$/, '') + lines[i].trim()
    }
    out.push({ name: m[1].trim(), value, sourceLine: start + 1, raw: lines[start].trim() })
  }
  return out
}

/* De un valor de DEFAULT concatenado devuelve el texto plano de todas las
   comillas unidas: "A|"+"B|" → "A|B|". */
function joinQuoted(value) {
  const parts = [...String(value).matchAll(/"([^"]*)"/g)].map((m) => m[1])
  if (parts.length) return parts.join('')
  return String(value).replace(/^"|"$/g, '')
}

/* Parsea las opciones de un catálogo (label=value|label2=value2|…). */
function parseCatalog(value) {
  const flat = joinQuoted(value)
  const opts = flat.split('|').map((o) => o.trim()).filter((o) => o !== '')
  return opts.map((opt) => {
    const eq = opt.indexOf('=')
    if (eq >= 0) return { label: opt.slice(0, eq).trim(), value: opt.slice(eq + 1).trim(), raw: opt }
    return { label: opt, value: opt, raw: opt }
  })
}

/* ============================================================================
   ANALIZADOR PRINCIPAL
   ============================================================================ */
export function analyzeSstDiagnostics(source, options = {}) {
  const hasClipboardAdapter = options.hasClipboardAdapter === true
  const providedFiles = (options.providedFiles || []).map((f) => String(f).toLowerCase())
  const lines = normalizeLines(source)
  const diagnostics = []
  const push = (d) => diagnostics.push(d)

  // ---- índices base ----------------------------------------------------------
  const defineByUpper = new Map() // UPPER → [{name, sourceLine, signature}]
  const malformedDefines = []
  const definedNames = new Set() // nombres tal cual (con #D)
  const definedBase = new Set() // nombre sin sufijo
  const subs = new Map() // NAME → sourceLine
  const labels = new Map() // NAME → sourceLine
  const readDest = new Map() // VAR → sourceLine (primer READ que la escribe)
  const assigned = new Set() // VAR asignada [X]=...
  const usageLines = new Map() // VAR → [sourceLine,...] (usos en cuerpo)
  const windowVars = new Map() // VAR → sourceLine (aparece dentro de WINDOW)

  let inWindow = false
  let inMenu = false
  let menuStart = 0

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const ln = i + 1
    if (isComment(line)) continue
    const nq = stripQuoted(line)

    // DEFINE (puede haber varios en una misma línea: DEFINE [a=..] DEFINE [b=..])
    if (/^\s*DEFINE\s*\[/i.test(line)) {
      const blocks = extractDefineBlocks(line)
      if (!blocks.length) {
        malformedDefines.push({ sourceLine: ln, raw: line.trim(), field: '', reason: 'incomplete_brackets' })
        continue
      }
      blocks.forEach((block) => {
        const parsed = parseDefineBlock(block)
        if (!parsed.ok) {
          malformedDefines.push({
            sourceLine: ln,
            raw: block.raw || line.trim(),
            field: parsed.name || '',
            reason: parsed.reason,
          })
          return
        }
        const name = parsed.name
        const sig = parsed.signature
        definedNames.add(name.toUpperCase())
        definedBase.add(baseName(name).toUpperCase())
        const key = name.toUpperCase()
        if (!defineByUpper.has(key)) defineByUpper.set(key, [])
        defineByUpper.get(key).push({
          name,
          sourceLine: ln,
          signature: sig,
          defineType: parsed.type,
          mand: parsed.mand,
          controlSuffix: parsed.controlSuffix,
          raw: block.raw || line.trim(),
        })
      })
      continue
    }
    // SUBROUTINE / SUB
    const sm = line.match(/^\s*(?:SUBROUTINE|SUB)\s+([A-Za-z_][A-Za-z0-9_]*)\s*:?/i)
    if (sm) { subs.set(sm[1].toUpperCase(), ln); continue }
    // Label  NOMBRE:
    const lm = line.match(/^\s*([A-Za-z_][A-Za-z0-9_]*):\s*$/)
    if (lm && !/^(IF|ELSE|ENDIF|THEN|WINDOW|ENDWINDOW|MENU|ENDMENU)$/i.test(lm[1])) {
      labels.set(lm[1].toUpperCase(), ln)
    }

    // WINDOW / MENU tracking
    if (/^\s*WINDOW\b/i.test(line)) inWindow = true
    if (/^\s*ENDWINDOW\b/i.test(line)) inWindow = false
    if (/^\s*MENU\b/i.test(line)) { inMenu = true; menuStart = ln }
    if (/^\s*ENDMENU\b/i.test(line)) inMenu = false

    // READ destinations:  READ F="..." ... [DEST]
    if (/^\s*READ\b/i.test(line)) {
      extractVarRefs(nq).forEach((v) => { if (!readDest.has(v.toUpperCase())) readDest.set(v.toUpperCase(), ln) })
    }

    // Asignaciones [X]="..." o [X]=[Y]
    const am = line.match(/\[([#@]?[A-Za-z_][A-Za-z0-9_#]*)\]\s*=/)
    if (am && !/^\s*(DEFINE|DEFAULT)\b/i.test(line)) assigned.add(am[1].toUpperCase())

    // Usos de variables (cuerpo, no declaraciones)
    if (!/^\s*(DEFINE|DEFAULT)\b/i.test(line)) {
      extractVarRefs(nq).forEach((v) => {
        const k = v.toUpperCase()
        if (!usageLines.has(k)) usageLines.set(k, [])
        usageLines.get(k).push(ln)
        if (inWindow && !windowVars.has(k)) windowVars.set(k, ln)
      })
    }
  }

  const defaults = collectDefaults(lines)

  // ==========================================================================
  // 1) MISSING_CSV_FILE — DEFAULT/READ que apunta a un CSV no provisto
  // ==========================================================================
  for (let i = 0; i < lines.length; i++) {
    if (isComment(lines[i])) continue
    const csv = lines[i].match(/F\s*=\s*"CSV:([^"]+)"/i) || lines[i].match(/=\s*"CSV:([^"]+)"/i)
    if (!csv) continue
    const file = csv[1].trim()
    if (hasProvidedFile(providedFiles, file)) continue
    const varRef = extractVarRefs(stripQuoted(lines[i]))[0] || ''
    push({
      code: 'MISSING_CSV_FILE',
      severity: 'error',
      field: varRef,
      file,
      missingFile: file,
      dependencyType: 'csv',
      message: `No se encontró el archivo requerido: ${file}`,
      sourceLine: i + 1,
      raw: lines[i].trim(),
    })
  }

  // ==========================================================================
  // 2) MISSING_GOTO_TARGET / MISSING_CALL_TARGET
  // ==========================================================================
  for (let i = 0; i < lines.length; i++) {
    if (isComment(lines[i])) continue
    const nq = stripQuoted(lines[i])
    let m
    const gotoRx = /\bGOTO\s+([A-Za-z_][A-Za-z0-9_]*)/gi
    while ((m = gotoRx.exec(nq)) !== null) {
      const t = m[1].toUpperCase()
      if (!labels.has(t) && !subs.has(t)) {
        push({ code: 'MISSING_GOTO_TARGET', severity: 'error', target: m[1],
          message: `El script usa GOTO ${m[1]}, pero no existe un destino definido con ese nombre.`,
          sourceLine: i + 1, raw: lines[i].trim() })
      }
    }
    const callRx = /\bCALL\s+([A-Za-z_][A-Za-z0-9_]*)/gi
    while ((m = callRx.exec(nq)) !== null) {
      const t = m[1].toUpperCase()
      if (!subs.has(t)) {
        push({ code: 'MISSING_CALL_TARGET', severity: 'error', target: m[1],
          message: `El script usa CALL ${m[1]}, pero no existe SUBROUTINE ${m[1]}.`,
          sourceLine: i + 1, raw: lines[i].trim() })
      }
    }
  }

  // ==========================================================================
  // 3) UNDEFINED_VARIABLE_REFERENCE — usada sin fuente de valor detectable
  // ==========================================================================
  usageLines.forEach((usedAt, key) => {
    if (key.startsWith('@')) return
    if (SYSTEM_VARS.has(key)) return
    const upperNoSuffix = baseName(key).toUpperCase()
    const hasDefine = definedNames.has(key) || definedBase.has(upperNoSuffix)
    const hasAssign = assigned.has(key)
    const hasRead = readDest.has(key)
    if (!hasDefine && !hasAssign && !hasRead) {
      const ln = usedAt[0]
      push({ code: 'UNDEFINED_VARIABLE_REFERENCE', severity: 'error', variable: key,
        message: `La variable ${key} se usa en el script, pero no existe DEFINE para ella.`,
        sourceLine: ln, raw: (lines[ln - 1] || '').trim() })
    }
  })

  // ==========================================================================
  // 4) CASE_INSENSITIVE_VARIABLE_COLLISION
  // ==========================================================================
  defineByUpper.forEach((entries) => {
    const spellings = [...new Set(entries.map((e) => e.name))]
    if (spellings.length > 1) {
      push({ code: 'CASE_INSENSITIVE_VARIABLE_COLLISION', severity: 'warning',
        variables: spellings,
        message: 'Existen variables que solo difieren por mayúsculas/minúsculas. Esto puede generar colisiones en el runtime.',
        sourceLines: entries.map((e) => e.sourceLine) })
    }
  })

  // ==========================================================================
  // 5) UNUSED_SUBROUTINE — SUB definida sin CALL local
  // ==========================================================================
  const calledSubs = new Set()
  lines.forEach((line) => {
    if (isComment(line)) return
    const m = stripQuoted(line).match(/\bCALL\s+([A-Za-z_][A-Za-z0-9_]*)/i)
    if (m) calledSubs.add(m[1].toUpperCase())
  })
  subs.forEach((ln, name) => {
    if (!calledSubs.has(name)) {
      push({ code: 'UNUSED_SUBROUTINE', severity: 'warning', subroutine: name,
        message: `La subrutina ${name} está definida, pero no se encontró ningún CALL local hacia ella.`,
        sourceLine: ln, raw: (lines[ln - 1] || '').trim() })
    }
  })

  // ==========================================================================
  // 6) MALFORMED_DEFINE_SIGNATURE (conservador para no inundar)
  // ==========================================================================
  malformedDefines.forEach((e) => {
    push({ code: 'MALFORMED_DEFINE_SIGNATURE', severity: 'warning',
      field: e.field,
      message: 'La definición DEFINE parece incompleta o no pudo extraerse correctamente.',
      sourceLine: e.sourceLine, raw: e.raw })
  })
  defineByUpper.forEach((entries) => {
    entries.forEach((e) => {
      const segs = String(e.signature).split(':')
      const type = (e.defineType || segs[0] || '').trim().toUpperCase()
      const mand = (e.mand || segs[2] || '').trim().toUpperCase()
      const unknownType = type !== '' && !DEFINE_TYPES.has(type)
      const badMand = mand !== '' && !DEFINE_MAND.has(mand)
      if (type === '' || badMand) {
        push({ code: 'MALFORMED_DEFINE_SIGNATURE', severity: 'warning',
          field: e.name,
          message: `La definición del campo ${e.name} parece incompleta o irregular.`,
          sourceLine: e.sourceLine, raw: e.raw })
      }
      if (unknownType) {
        push({ code: 'UNKNOWN_DEFINE_TYPE', severity: 'warning',
          field: e.name,
          type,
          message: `El tipo ${type} del campo ${e.name} no es reconocido; se recomienda tratarlo como texto.`,
          sourceLine: e.sourceLine, raw: e.raw })
      }
    })
  })

  // Catálogos por campo (para reglas 7 y 8)
  const catalogByName = new Map()
  defaults.forEach((d) => {
    const flat = joinQuoted(d.value)
    if (/^\s*CSV:/i.test(flat)) return // CSV no es catálogo inline
    if (!flat.includes('|') && !flat.includes('=')) return
    const opts = parseCatalog(d.value)
    if (opts.length) catalogByName.set(d.name.toUpperCase(), { opts, sourceLine: d.sourceLine, raw: d.raw })
  })

  // ==========================================================================
  // 7) DUPLICATED_CATALOG_OPTION
  // ==========================================================================
  catalogByName.forEach((cat) => {
    const seen = new Set()
    const dups = new Set()
    cat.opts.forEach((o) => {
      const norm = o.label.replace(/\s+/g, ' ').trim().toUpperCase()
      if (seen.has(norm)) dups.add(o.label.trim())
      seen.add(norm)
    })
    if (dups.size) {
      push({ code: 'DUPLICATED_CATALOG_OPTION', severity: 'warning',
        options: [...dups],
        message: 'El catálogo contiene opciones duplicadas.',
        sourceLine: cat.sourceLine, raw: cat.raw })
    }
  })

  // ==========================================================================
  // 8) CATALOG_COMPARISON_VALUE_NOT_FOUND
  // ==========================================================================
  for (let i = 0; i < lines.length; i++) {
    if (isComment(lines[i])) continue
    const cm = lines[i].match(/IF\s*\[([#@]?[A-Za-z_][A-Za-z0-9_#]*)\]\s*=\s*"([^"]*)"/i)
    if (!cm) continue
    const field = cm[1].toUpperCase()
    const lit = cm[2].trim().toUpperCase()
    const cat = catalogByName.get(field)
    if (!cat) continue
    const ok = cat.opts.some((o) => o.value.toUpperCase() === lit || o.label.replace(/\s+/g, ' ').trim().toUpperCase() === lit)
    if (!ok) {
      push({ code: 'CATALOG_COMPARISON_VALUE_NOT_FOUND', severity: 'warning',
        field: cm[1], compared: cm[2],
        message: 'El campo se compara con un valor que no existe en su catálogo.',
        sourceLine: i + 1, raw: lines[i].trim() })
    }
  }

  // ==========================================================================
  // 9) BUTTON_WITHOUT_ACTION
  // ==========================================================================
  const btnActions = new Set()
  lines.forEach((line) => {
    if (isComment(line)) return
    const m = line.match(/\[@BTN_PRSD\]\s*=\s*"([^"]+)"/i)
    if (m) btnActions.add(m[1].trim().toUpperCase())
  })
  for (let i = 0; i < lines.length; i++) {
    if (isComment(lines[i])) continue
    const rx = /\[@BTN_L\d+_\d+\]\s*=\s*"([^"]+)"/gi
    let m
    while ((m = rx.exec(lines[i])) !== null) {
      const text = m[1].trim()
      if (/^-+$/.test(text)) continue
      if (!btnActions.has(text.toUpperCase())) {
        push({ code: 'BUTTON_WITHOUT_ACTION', severity: 'warning', button: text,
          message: `El botón "${text}" está definido, pero no tiene acción asociada.`,
          sourceLine: i + 1, raw: lines[i].trim() })
      }
    }
  }

  // ==========================================================================
  // 10) MISSING_CLIPBOARD_ADAPTER
  // ==========================================================================
  if (!hasClipboardAdapter) {
    const clip = []
    for (let i = 0; i < lines.length; i++) {
      if (isComment(lines[i])) continue
      if (/(READ|WRITE)\s+F\s*=\s*"CLIPBOARD:/i.test(lines[i])) clip.push(i + 1)
    }
    if (clip.length) {
      push({ code: 'MISSING_CLIPBOARD_ADAPTER', severity: 'error',
        occurrences: clip.length, occurrenceLines: clip,
        message: `El script usa CLIPBOARD (${clip.length} uso${clip.length > 1 ? 's' : ''}), pero el runtime no tiene adaptador configurado para leer o escribir portapapeles.`,
        sourceLine: clip[0], raw: (lines[clip[0] - 1] || '').trim() })
    }
  }

  // ==========================================================================
  // 11) VISIBLE_FIELD_WITHOUT_ACTIVE_OUTPUT_USE
  // ==========================================================================
  windowVars.forEach((wln, key) => {
    if (key.startsWith('@')) return
    const uses = usageLines.get(key) || []
    // uso "activo" = aparece en una línea fuera de WINDOW (comando/remark/IF/WRITE)
    const activeUse = uses.some((ln) => {
      const l = lines[ln - 1] || ''
      return !/^\s*(WINDOW|ENDWINDOW)/i.test(l) && !isFieldDisplayLine(l, key)
    })
    if (!activeUse) {
      push({ code: 'VISIBLE_FIELD_WITHOUT_ACTIVE_OUTPUT_USE', severity: 'warning', variable: key,
        message: `El campo ${key} es visible para captura, pero no se detectó uso activo en la salida del script.`,
        sourceLine: wln, raw: (lines[wln - 1] || '').trim() })
    }
  })

  // ==========================================================================
  // 12) READ_RESULT_NOT_USED
  // ==========================================================================
  readDest.forEach((rln, key) => {
    if (key.startsWith('@')) return
    const uses = (usageLines.get(key) || []).filter((ln) => ln !== rln)
    // descartar reusos dentro de otros READ de la misma variable
    const realUse = uses.some((ln) => !/^\s*READ\b/i.test(lines[ln - 1] || ''))
    if (!realUse) {
      push({ code: 'READ_RESULT_NOT_USED', severity: 'warning', variable: key,
        message: `La variable ${key} recibe valor desde READ, pero no se detectó uso posterior activo.`,
        sourceLine: rln, raw: (lines[rln - 1] || '').trim() })
    }
  })

  // ==========================================================================
  // 13) MENU_SEPARATOR_ITEM
  // ==========================================================================
  {
    let insideMenu = false
    for (let i = 0; i < lines.length; i++) {
      if (isComment(lines[i])) continue
      if (/^\s*MENU\b/i.test(lines[i])) insideMenu = true
      else if (/^\s*ENDMENU\b/i.test(lines[i])) insideMenu = false
      else if (insideMenu) {
        const m = lines[i].match(/^\s*"(-{1,})"\s*$/)
        if (m) {
          push({ code: 'MENU_SEPARATOR_ITEM', severity: 'info',
            message: 'El elemento de menú parece ser un separador visual y no debe tratarse como opción ejecutable.',
            sourceLine: i + 1, raw: lines[i].trim() })
        }
      }
    }
  }

  // ==========================================================================
  // 14) SYSTEM_ERROR_DERIVED_FLAG
  // ==========================================================================
  for (let i = 0; i < lines.length; i++) {
    if (isComment(lines[i])) continue
    if (!/\[@SYSTEM_ERROR\]\s*(=|<>)/i.test(lines[i])) continue
    // buscar asignación de bandera en la misma línea o en la siguiente activa
    const scan = [lines[i], lines[i + 1] || '']
    for (let k = 0; k < scan.length; k++) {
      const fm = scan[k].match(/\[([A-Za-z_][A-Za-z0-9_#]*)\]\s*=\s*"(SI|NO|Y|N|1|0)"/i)
      if (fm) {
        push({ code: 'SYSTEM_ERROR_DERIVED_FLAG', severity: 'info', variable: fm[1],
          message: `La variable ${fm[1]} se comporta como bandera de control derivada de @SYSTEM_ERROR y no debe generarse como campo visible.`,
          sourceLine: i + 1 + k, raw: scan[k].trim() })
        break
      }
    }
  }

  // ==========================================================================
  // 15/16/17) SSR CTCE / CTCM / FOID
  // ==========================================================================
  for (let i = 0; i < lines.length; i++) {
    if (isComment(lines[i])) continue
    const line = lines[i]
    if (/CTCE\//i.test(line) && /\[[A-Za-z_][A-Za-z0-9_#]*\]/.test(line)) {
      push({ code: 'SSR_CTCE_EMAIL_WITHOUT_NORMALIZATION', severity: 'warning',
        message: 'El SSR CTCE usa email construido con variables, pero no se detectó normalización automática de caracteres especiales.',
        sourceLine: i + 1, raw: line.trim() })
    }
    if (/CTCM\//i.test(line) && /\[[A-Za-z_][A-Za-z0-9_#]*\]/.test(line)) {
      push({ code: 'SSR_CTCM_PHONE_WITHOUT_FORMAT_VALIDATION', severity: 'warning',
        message: 'El SSR CTCM usa un teléfono capturado, pero no se detectó validación de formato.',
        sourceLine: i + 1, raw: line.trim() })
    }
    if (/FOID\/|3FOID\//i.test(line) && /\[[A-Za-z_][A-Za-z0-9_#]*\]/.test(line)) {
      push({ code: 'SSR_FOID_SOURCE_NOT_CONFIRMED', severity: 'warning',
        message: 'El SSR FOID usa una fuente que puede no haber sido confirmada o leída correctamente.',
        sourceLine: i + 1, raw: line.trim() })
    }
  }

  // ==========================================================================
  // 18) MISSING_SPAWN_TARGET
  // ==========================================================================
  for (let i = 0; i < lines.length; i++) {
    if (isComment(lines[i])) continue
    const m = lines[i].match(/\bSPAWN\s+"?([A-Za-z0-9_.:\\/ -]+?)"?\s*$/i)
    if (m) {
      const target = m[1].trim()
      if (hasProvidedFile(providedFiles, target)) continue
      push({ code: 'MISSING_SPAWN_TARGET', severity: 'error', target, missingFile: target, dependencyType: 'spawn',
        message: `El script usa SPAWN hacia un recurso externo (${target}) que no fue proporcionado.`,
        sourceLine: i + 1, raw: lines[i].trim() })
    }
  }

  // Orden estable: por línea y luego por severidad
  diagnostics.sort((a, b) => (a.sourceLine || 0) - (b.sourceLine || 0))
  return diagnostics
}

/* Una línea de "display" del campo dentro de la ventana: LABEL: [var] */
function isFieldDisplayLine(line, key) {
  const m = String(line).match(/\[([#@]?[A-Za-z_][A-Za-z0-9_#]*)\]/)
  return m && m[1].toUpperCase() === key && /:\s*\[/.test(line)
}

/* Resumen por severidad (para badges del modal). */
export function summarizeDiagnostics(diagnostics) {
  const summary = { error: 0, warning: 0, info: 0, total: (diagnostics || []).length }
  ;(diagnostics || []).forEach((d) => {
    if (d.severity === 'error') summary.error++
    else if (d.severity === 'warning') summary.warning++
    else summary.info++
  })
  return summary
}

/* Metadatos legibles por código (título + pista de acción) para el modal/UI. */
export const DIAGNOSTIC_META = {
  MISSING_CSV_FILE: { title: 'Archivo CSV faltante', hint: 'Adjuntá el CSV o el select quedará vacío.' },
  MISSING_GOTO_TARGET: { title: 'GOTO sin destino', hint: 'Falta el label o la subrutina destino.' },
  MISSING_CALL_TARGET: { title: 'CALL sin subrutina', hint: 'No existe SUBROUTINE con ese nombre.' },
  UNDEFINED_VARIABLE_REFERENCE: { title: 'Variable sin definir', hint: 'Se usa sin DEFINE ni fuente de valor.' },
  CASE_INSENSITIVE_VARIABLE_COLLISION: { title: 'Colisión mayús/minús', hint: 'Variables que solo difieren por caso.' },
  UNUSED_SUBROUTINE: { title: 'Subrutina sin uso', hint: 'Definida pero sin CALL local.' },
  MALFORMED_DEFINE_SIGNATURE: { title: 'DEFINE irregular', hint: 'Firma incompleta o tipo desconocido.' },
  DUPLICATED_CATALOG_OPTION: { title: 'Opción duplicada', hint: 'El catálogo repite una opción.' },
  CATALOG_COMPARISON_VALUE_NOT_FOUND: { title: 'Comparación fuera de catálogo', hint: 'El IF compara contra un valor inexistente.' },
  BUTTON_WITHOUT_ACTION: { title: 'Botón sin acción', hint: 'No hay rama para ese botón.' },
  MISSING_CLIPBOARD_ADAPTER: { title: 'CLIPBOARD sin adaptador', hint: 'El runtime necesita soporte de portapapeles.' },
  VISIBLE_FIELD_WITHOUT_ACTIVE_OUTPUT_USE: { title: 'Campo visible sin uso', hint: 'Se captura pero no impacta la salida.' },
  READ_RESULT_NOT_USED: { title: 'READ sin uso', hint: 'La lectura no se usa después.' },
  MENU_SEPARATOR_ITEM: { title: 'Separador de menú', hint: 'Elemento visual, no ejecutable.' },
  SYSTEM_ERROR_DERIVED_FLAG: { title: 'Bandera @SYSTEM_ERROR', hint: 'Flag de control, no campo visible.' },
  SSR_CTCE_EMAIL_WITHOUT_NORMALIZATION: { title: 'CTCE sin normalizar', hint: 'Normalizá el email para Sabre.' },
  SSR_CTCM_PHONE_WITHOUT_FORMAT_VALIDATION: { title: 'CTCM sin validar', hint: 'Validá el teléfono (solo dígitos).' },
  SSR_FOID_SOURCE_NOT_CONFIRMED: { title: 'FOID sin confirmar', hint: 'La fuente puede venir vacía.' },
  MISSING_SPAWN_TARGET: { title: 'SPAWN externo', hint: 'Recurso externo no provisto.' },
  COVERAGE_MISSING_FIELD: { title: 'Campo del fuente sin form', hint: 'Se captura en WINDOW pero no está en forms[].' },
  COVERAGE_MISSING_FAMILY: { title: 'Escritura del fuente sin cubrir', hint: 'Family de escritura sin comando en el data.json.' },
  COVERAGE_MISSING_READ: { title: 'Lectura sin onLoad/prefill', hint: 'El fuente lee del PNR y el data.json no.' },
  COVERAGE_INVENTED_FAMILY: { title: 'Comando inventado', hint: 'El data.json emite una family que el fuente no tiene.' },
  COVERAGE_AUTO_COMMIT: { title: 'ER automático (WARN_NO_COMMIT)', hint: 'El fuente no graba; el runtime agregará ER.' },
  COVERAGE_MISSING_ALERTS: { title: 'Alertas perdidas', hint: 'El fuente avisa al usuario y el data.json no trae alerts[].' },
}
